/**
 * useAlertActions Hook
 *
 * Manages alert CRUD operations with optimistic updates.
 * Handles dismiss, resolve, and notify actions with rollback on failure.
 *
 * Uses the alerts service layer for API operations.
 * @see ../../services/alerts for API implementation
 */

import { useState, useCallback, useEffect } from 'react';
import { useToastHelpers } from '../../components/feedback/ToastProvider';
import { usePreservingRevalidator } from '../core/usePreservingRevalidator';
import * as alertsApi from '../../services/alerts';
import { AlertApiError } from '../../services/alerts';
import type { EnhancedAlert, NotificationChannel } from '../../types/dashboard';

/**
 * State and handlers for alert action modals
 */
export interface AlertActionModalState {
  showResolveModal: boolean;
  showDismissModal: boolean;
  showNotifyModal: boolean;
  selectedAlert: EnhancedAlert | null;
  selectedChannelIds: number[];
}

/**
 * Return type for useAlertActions hook
 */
export interface UseAlertActionsReturn {
  // Optimistic state
  optimisticAlerts: EnhancedAlert[];
  dismissingAlerts: Set<number>;
  resolvingAlerts: Set<number>;
  notifyingAlerts: Set<number>;

  // Modal state
  modalState: AlertActionModalState;

  // Direct action handlers (with optimistic updates)
  handleDismissAlert: (alertId: number) => Promise<void>;
  handleResolveAlert: (alertId: number) => Promise<void>;

  // Confirmation modal handlers
  openResolveConfirmation: (alert: EnhancedAlert) => void;
  openDismissConfirmation: (alert: EnhancedAlert) => void;
  openNotifyConfirmation: (alert: EnhancedAlert) => void;
  handleConfirmedResolve: () => Promise<void>;
  handleConfirmedDismiss: () => Promise<void>;
  handleConfirmedNotify: () => Promise<void>;
  handleChannelSelectionChange: (channelIds: number[]) => void;
  closeModals: () => void;

  // Route-based modal completion handlers (modals handle API calls themselves)
  onAlertResolved: (alertId: number) => void;
  onAlertDismissed: (alertId: number) => void;
  onAlertNotified: (alertId: number, channelIds: number[]) => void;
}

/**
 * Hook for managing alert actions with optimistic updates
 *
 * @param initialAlerts - Initial alerts array from loader
 * @param notificationChannels - Available notification channels for the shop
 * @returns Alert state and action handlers
 */
export function useAlertActions(
  initialAlerts: EnhancedAlert[],
  notificationChannels: NotificationChannel[] = []
): UseAlertActionsReturn {
  // Use scroll-preserving revalidator to prevent scroll jump after actions
  const revalidator = usePreservingRevalidator();
  const toast = useToastHelpers();

  // Optimistic alerts state for instant UI feedback
  const [optimisticAlerts, setOptimisticAlerts] = useState<EnhancedAlert[]>(initialAlerts);
  const [dismissingAlerts, setDismissingAlerts] = useState<Set<number>>(new Set());
  const [resolvingAlerts, setResolvingAlerts] = useState<Set<number>>(new Set());
  const [notifyingAlerts, setNotifyingAlerts] = useState<Set<number>>(new Set());

  // Alert action confirmation modal state
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [selectedAlertForAction, setSelectedAlertForAction] = useState<EnhancedAlert | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);

  // Sync optimistic state with loader data when it changes
  useEffect(() => {
    setOptimisticAlerts(initialAlerts);
  }, [initialAlerts]);

  // Pre-select all verified and enabled channels when opening notify modal
  const availableChannels = notificationChannels.filter(c => c.enabled && c.is_verified);

  /**
   * Dismiss an alert with optimistic update
   */
  const handleDismissAlert = useCallback(async (alertId: number) => {
    // Store original alert for rollback
    const originalAlert = optimisticAlerts.find(a => a.id === alertId);
    if (!originalAlert) return;

    // Optimistic update: Remove from UI immediately
    setOptimisticAlerts(prev => prev.filter(a => a.id !== alertId));
    setDismissingAlerts(prev => new Set(prev).add(alertId));

    try {
      await alertsApi.dismissAlert(alertId);

      // Success: Show confirmation
      toast.success('Alert dismissed');

      // Trigger revalidation to sync with server
      revalidator.revalidate();
    } catch (error) {
      // Dismiss failed - rollback and show toast error

      // Rollback: Add alert back to UI
      setOptimisticAlerts(prev => [...prev, originalAlert].sort((a, b) => a.id - b.id));

      // Show error toast
      toast.error(error instanceof AlertApiError ? error.message : 'Failed to dismiss alert. Please try again.');
    } finally {
      setDismissingAlerts(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  }, [optimisticAlerts, toast, revalidator]);

  /**
   * Resolve an alert with optimistic update
   */
  const handleResolveAlert = useCallback(async (alertId: number) => {
    // Store original alert for rollback
    const originalAlert = optimisticAlerts.find(a => a.id === alertId);
    if (!originalAlert) return;

    // Optimistic update: Remove from UI immediately
    setOptimisticAlerts(prev => prev.filter(a => a.id !== alertId));
    setResolvingAlerts(prev => new Set(prev).add(alertId));

    try {
      await alertsApi.resolveAlert(alertId);

      // Success: Show confirmation
      toast.success('Alert resolved');

      // Trigger revalidation to sync with server
      revalidator.revalidate();
    } catch (error) {
      // Resolve failed - rollback and show toast error

      // Rollback: Add alert back to UI
      setOptimisticAlerts(prev => [...prev, originalAlert].sort((a, b) => a.id - b.id));

      // Show error toast
      toast.error(error instanceof AlertApiError ? error.message : 'Failed to resolve alert. Please try again.');
    } finally {
      setResolvingAlerts(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  }, [optimisticAlerts, toast, revalidator]);

  /**
   * Open resolve confirmation modal
   */
  const openResolveConfirmation = useCallback((alert: EnhancedAlert) => {
    setSelectedAlertForAction(alert);
    setShowResolveModal(true);
  }, []);

  /**
   * Open dismiss confirmation modal
   */
  const openDismissConfirmation = useCallback((alert: EnhancedAlert) => {
    setSelectedAlertForAction(alert);
    setShowDismissModal(true);
  }, []);

  /**
   * Open notify confirmation modal with pre-selected channels
   */
  const openNotifyConfirmation = useCallback((alert: EnhancedAlert) => {
    setSelectedAlertForAction(alert);
    // Pre-select all available (verified + enabled) channels
    setSelectedChannelIds(availableChannels.map(c => c.id));
    setShowNotifyModal(true);
  }, [availableChannels]);

  /**
   * Handle channel selection change in notify modal
   */
  const handleChannelSelectionChange = useCallback((channelIds: number[]) => {
    setSelectedChannelIds(channelIds);
  }, []);

  /**
   * Handle confirmed resolve action from modal
   */
  const handleConfirmedResolve = useCallback(async () => {
    if (!selectedAlertForAction) return;
    setShowResolveModal(false);
    await handleResolveAlert(selectedAlertForAction.id);
    setSelectedAlertForAction(null);
  }, [selectedAlertForAction, handleResolveAlert]);

  /**
   * Handle confirmed dismiss action from modal
   */
  const handleConfirmedDismiss = useCallback(async () => {
    if (!selectedAlertForAction) return;
    setShowDismissModal(false);
    await handleDismissAlert(selectedAlertForAction.id);
    setSelectedAlertForAction(null);
  }, [selectedAlertForAction, handleDismissAlert]);

  /**
   * Handle confirmed notify action from modal
   * Sends notification through selected channels
   */
  const handleConfirmedNotify = useCallback(async () => {
    if (!selectedAlertForAction || selectedChannelIds.length === 0) return;

    const alertId = selectedAlertForAction.id;
    setShowNotifyModal(false);
    setNotifyingAlerts(prev => new Set(prev).add(alertId));

    try {
      const result = await alertsApi.notifyAlert({
        alertId,
        channelIds: selectedChannelIds,
      });

      // Show success message with details
      const sentCount = result.sent_to?.length ?? 0;
      const skippedCount = result.skipped?.length ?? 0;

      if (sentCount > 0 && skippedCount === 0) {
        toast.success(`Notification sent to ${sentCount} channel${sentCount > 1 ? 's' : ''}`);
      } else if (sentCount > 0 && skippedCount > 0) {
        toast.info(`Sent to ${sentCount} channel${sentCount > 1 ? 's' : ''}, ${skippedCount} skipped (recently notified)`);
      } else if (skippedCount > 0) {
        toast.info('All channels skipped - notifications were recently sent');
      }
    } catch (error) {
      // Notification send failed - show toast error
      toast.error(error instanceof AlertApiError ? error.message : 'Failed to send notification');
    } finally {
      setNotifyingAlerts(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
      setSelectedAlertForAction(null);
      setSelectedChannelIds([]);
    }
  }, [selectedAlertForAction, selectedChannelIds, toast]);

  /**
   * Close all modals and reset selected alert
   */
  const closeModals = useCallback(() => {
    setShowResolveModal(false);
    setShowDismissModal(false);
    setShowNotifyModal(false);
    setSelectedAlertForAction(null);
    setSelectedChannelIds([]);
  }, []);

  /**
   * Handler for when route-based resolve modal completes successfully.
   * The modal handles the API call, this just triggers data refresh.
   */
  const onAlertResolved = useCallback((alertId: number) => {
    // Remove from optimistic state
    setOptimisticAlerts(prev => prev.filter(a => a.id !== alertId));
    // Show success toast
    toast.success('Alert resolved');
    // Trigger data refresh
    revalidator.revalidate();
  }, [toast, revalidator]);

  /**
   * Handler for when route-based dismiss modal completes successfully.
   * The modal handles the API call, this just triggers data refresh.
   */
  const onAlertDismissed = useCallback((alertId: number) => {
    // Remove from optimistic state
    setOptimisticAlerts(prev => prev.filter(a => a.id !== alertId));
    // Show success toast
    toast.success('Alert dismissed');
    // Trigger data refresh
    revalidator.revalidate();
  }, [toast, revalidator]);

  /**
   * Handler for when route-based notify modal completes successfully.
   * The modal handles the API call, this just triggers data refresh.
   */
  const onAlertNotified = useCallback((alertId: number, channelIds: number[]) => {
    // Update notification timestamp in optimistic state
    setOptimisticAlerts(prev =>
      prev.map(a =>
        a.id === alertId
          ? { ...a, last_notification_at: new Date().toISOString() }
          : a
      )
    );
    // Show success toast
    const channelCount = channelIds.length;
    toast.success(`Notification sent to ${channelCount} channel${channelCount !== 1 ? 's' : ''}`);
    // Trigger data refresh
    revalidator.revalidate();
  }, [toast, revalidator]);

  return {
    // Optimistic state
    optimisticAlerts,
    dismissingAlerts,
    resolvingAlerts,
    notifyingAlerts,

    // Modal state
    modalState: {
      showResolveModal,
      showDismissModal,
      showNotifyModal,
      selectedAlert: selectedAlertForAction,
      selectedChannelIds,
    },

    // Direct action handlers
    handleDismissAlert,
    handleResolveAlert,

    // Confirmation modal handlers
    openResolveConfirmation,
    openDismissConfirmation,
    openNotifyConfirmation,
    handleConfirmedResolve,
    handleConfirmedDismiss,
    handleConfirmedNotify,
    handleChannelSelectionChange,
    closeModals,

    // Route-based modal completion handlers
    onAlertResolved,
    onAlertDismissed,
    onAlertNotified,
  };
}
